import { popcount } from "../../ariadne/persist";
import { unifiedDataStore } from "../../services/UnifiedDataStore";
import type { GameReport } from "../../utils/gameReportUtils";
import type { Achievement } from "./achievement.types";
import { firstLightAchievement } from "./definitions/firstLight.definition";
import { halfTheMapAchievement } from "./definitions/halfTheMap.definition";
import { surveyorAchievement } from "./definitions/surveyor.definition";

/**
 * The atlas milestones (spec 2026-10-20 §3.4, D14): awarded from the number
 * of her words the player has stood on (`AriadneState.seen`), the number the
 * vision says matters. Counts, not shares, so they survive a vocabulary
 * revision; "half" is computed from the live graph's size.
 */
export const ATLAS_MILESTONES: ReadonlyArray<{
  achievement: Achievement;
  threshold: (n: number) => number;
}> = [
  { achievement: firstLightAchievement, threshold: () => 100 },
  { achievement: surveyorAchievement, threshold: () => 1000 },
  { achievement: halfTheMapAchievement, threshold: (n) => Math.ceil(n / 2) },
];

/** Pure: every milestone reached by `seen` words of `n`, in order. */
export const atlasMilestones = (seen: number, n: number): Achievement[] =>
  ATLAS_MILESTONES.filter((m) => seen >= m.threshold(n)).map(
    (m) => m.achievement,
  );

/**
 * After a game she learned from: unlock each milestone the atlas has reached,
 * once, and let the report lead with any first-time unlock. Never throws.
 */
export const applyAtlasAwards = async (
  report: GameReport,
  seen: Uint8Array,
  n: number,
): Promise<void> => {
  try {
    for (const achievement of atlasMilestones(popcount(seen), n)) {
      if (await unifiedDataStore.unlockAchievement(achievement.id)) {
        report.earnedAchievements = [
          ...(report.earnedAchievements ?? []),
          achievement,
        ];
        report.newAchievementIds = [
          ...(report.newAchievementIds ?? []),
          achievement.id,
        ];
      }
    }
  } catch (error) {
    console.error("Failed to award atlas milestones:", error);
  }
};
